import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Editor} from '@tiptap/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FormatBoldIcon from '@material-ui/icons/FormatBold'
import FormatItalicIcon from '@material-ui/icons/FormatItalic'
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined'
import StrikethroughSIcon from '@material-ui/icons/StrikethroughS'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import FormatQuoteIcon from '@material-ui/icons/FormatQuote'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import FormatClearIcon from '@material-ui/icons/FormatClear'
import LinkIcon from '@material-ui/icons/Link'
import ImageIcon from '@material-ui/icons/Image'
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary'
import CodeIcon from '@material-ui/icons/Code'
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease'
import FormatIndentDecreaseIcon from '@material-ui/icons/FormatIndentDecrease'
import TableChartIcon from '@material-ui/icons/TableChart'
import {
  ToolbarItem,
  ObvioVariable,
  EditorTheme,
  CUSTOM_LINK_MAP,
  CUSTOM_DATE_TIME_VALUE,
} from './types'
import CustomButtonToolbar from './CustomButtonToolbar'
import ToolbarPopover from './ToolbarPopover'
import CustomDateTimePopover from './CustomDateTimePopover'
import TableGridPicker from './TableGridPicker'
import FontFamilyPicker from './FontFamilyPicker'
import TableContextToolbar from './TableContextToolbar'
import ImageWrapButton from './ImageWrapButton'
import ColorPicker from './ColorPicker'

type MenuBarProps = {
  editor: Editor
  onImageUpload: (file: File) => void
  toolbar: ToolbarItem[]
  variables?: ObvioVariable[]
  customLinks?: string[]
  theme: EditorTheme
  onThemeChange: (theme: EditorTheme) => void
}

export default function MenuBar({
  editor,
  onImageUpload,
  toolbar,
  variables,
  customLinks,
  theme,
  onThemeChange,
}: MenuBarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [customDateTimeEl, setCustomDateTimeEl] = useState<HTMLElement | null>(
    null,
  )

  const has = useMemo(() => {
    const set = new Set<ToolbarItem>(toolbar)
    return (item: ToolbarItem) => set.has(item)
  }, [toolbar])

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        onImageUpload(file)
      }
      // Reset so same file can be re-selected
      e.target.value = ''
    },
    [onImageUpload],
  )

  const handleLinkSubmit = useCallback(
    (href: string) => {
      editor.chain().focus().extendMarkRange('link').setLink({href}).run()
    },
    [editor],
  )

  const handleLinkRemove = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  }, [editor])

  const handleMediaEmbedSubmit = useCallback(
    (url: string) => {
      editor.chain().focus().setMediaEmbed({src: url}).run()
    },
    [editor],
  )

  const handleHtmlEmbedSubmit = useCallback(
    (html: string) => {
      editor.chain().focus().setHtmlEmbed({html}).run()
    },
    [editor],
  )

  const linkPresets = useMemo(() => {
    if (!customLinks || customLinks.length === 0) return undefined
    return customLinks
      .map((name) => {
        const href = CUSTOM_LINK_MAP[name]
        if (href) return {label: name, value: href}
        return null
      })
      .filter(Boolean) as {label: string; value: string}[]
  }, [customLinks])

  const handleInsertCustomButton = useCallback(() => {
    editor.chain().focus().insertCustomButton().run()
  }, [editor])

  const handleVariableChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      if (value === CUSTOM_DATE_TIME_VALUE) {
        setCustomDateTimeEl(e.target)
      } else if (value) {
        editor.chain().focus().insertObvioVariable(value).run()
      }
      // Reset select
      e.target.value = ''
    },
    [editor],
  )

  const handleCustomDateTimeSubmit = useCallback(
    (variable: string) => {
      editor.chain().focus().insertObvioVariable(variable).run()
      setCustomDateTimeEl(null)
    },
    [editor],
  )

  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || ''

  let currentFontSize = editor.getAttributes('textStyle').fontSize || ''
  if (!currentFontSize) {
    try {
      const {from} = editor.state.selection
      const domAtPos = editor.view.domAtPos(from)
      const node =
        domAtPos.node instanceof HTMLElement
          ? domAtPos.node
          : domAtPos.node.parentElement
      if (node) {
        currentFontSize = window.getComputedStyle(node).fontSize
      }
    } catch {
      // ignore
    }
  }

  const defaultFontColor = theme === 'Light' ? '#000000' : '#ffffff'
  const currentFontColor =
    editor.getAttributes('textStyle').color || defaultFontColor
  const currentHighlight = editor.getAttributes('highlight').color || '#ffff00'

  const showHeadingGroup =
    has('heading') || has('customFontFamily') || has('customFontSize')
  const showColorGroup = has('fontColor') || has('fontBackgroundColor')
  const showAlignmentGroup = has('alignment')
  const showFormattingGroup =
    has('bold') ||
    has('italic') ||
    has('underline') ||
    has('strikethrough') ||
    has('superscript') ||
    has('removeFormat')
  const showInsertGroup =
    has('link') || has('mediaEmbed') || has('imageUpload') || has('htmlEmbed')
  const showListGroup =
    has('bulletedList') ||
    has('numberedList') ||
    has('outdent') ||
    has('indent')
  const showBlockGroup = has('blockQuote') || has('insertTable')
  const showVariableGroup =
    has('obvioVariable') && variables && variables.length > 0
  const showCustomButtonGroup = has('insertCustomButton')
  const showThemeGroup = has('customTheme')

  const selectedNode = editor.state.doc.nodeAt(editor.state.selection.from)
  const isCustomButtonSelected = selectedNode?.type.name === 'customButton'
  const isImageSelected = selectedNode?.type.name === 'image'
  const isTableActive = editor.isActive('table')

  return (
    <>
      <div
        className="html-editor-toolbar"
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Heading / Font / Size */}
        {showHeadingGroup && (
          <div className="html-editor-toolbar__group">
            {has('heading') && (
              <select
                className="html-editor-toolbar__select"
                value={
                  editor.isActive('heading', {level: 1})
                    ? '1'
                    : editor.isActive('heading', {level: 2})
                    ? '2'
                    : editor.isActive('heading', {level: 3})
                    ? '3'
                    : editor.isActive('heading', {level: 4})
                    ? '4'
                    : '0'
                }
                onChange={(e) => {
                  const level = parseInt(e.target.value)
                  if (level === 0) {
                    editor.chain().focus().setParagraph().run()
                  } else {
                    editor
                      .chain()
                      .focus()
                      .toggleHeading({level: level as 1 | 2 | 3 | 4})
                      .run()
                  }
                }}
              >
                <option value="0">Paragraph</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
                <option value="4">Heading 4</option>
              </select>
            )}

            {has('heading') &&
              (has('customFontFamily') || has('customFontSize')) && (
                <div className="html-editor-toolbar__divider" />
              )}

            {has('customFontFamily') && (
              <FontFamilyPicker
                value={currentFontFamily}
                onChange={(family) => {
                  if (!family) {
                    editor.chain().focus().unsetFontFamily().run()
                  } else {
                    editor.chain().focus().setFontFamily(family).run()
                  }
                }}
              />
            )}

            {has('customFontSize') && (
              <input
                className="html-editor-toolbar__size-input"
                type="number"
                min="1"
                max="200"
                placeholder="Size"
                value={currentFontSize ? parseInt(currentFontSize) : ''}
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) {
                    editor.chain().unsetFontSize().run()
                  } else {
                    editor.chain().setFontSize(`${value}px`).run()
                  }
                }}
              />
            )}
          </div>
        )}

        {/* Colors */}
        {showColorGroup && (
          <div className="html-editor-toolbar__group">
            {has('fontColor') && (
              <Tooltip title="Font Color">
                <span>
                  <ColorPicker
                    value={currentFontColor}
                    aria-label="font color"
                    onChange={(color) => {
                      editor.chain().focus().setColor(color).run()
                    }}
                    anchor={({onClick, hexValue}) => (
                      <button
                        type="button"
                        className="html-editor-toolbar__color-label"
                        onClick={onClick}
                        aria-label="font color"
                      >
                        <span
                          className="html-editor-toolbar__color-icon"
                          style={{color: hexValue}}
                        >
                          A
                        </span>
                      </button>
                    )}
                  />
                </span>
              </Tooltip>
            )}

            {has('fontBackgroundColor') && (
              <Tooltip title="Background Color">
                <span>
                  <ColorPicker
                    value={currentHighlight}
                    aria-label="background color"
                    onChange={(color) => {
                      editor.chain().focus().toggleHighlight({color}).run()
                    }}
                    anchor={({onClick, hexValue}) => (
                      <button
                        type="button"
                        className="html-editor-toolbar__color-label"
                        onClick={onClick}
                        aria-label="background color"
                      >
                        <span
                          className="html-editor-toolbar__color-icon html-editor-toolbar__color-icon--bg"
                          style={{backgroundColor: hexValue}}
                        >
                          A
                        </span>
                      </button>
                    )}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        )}

        {/* Alignment */}
        {showAlignmentGroup && (
          <div className="html-editor-toolbar__group">
            <Tooltip title="Align Left">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('left').run()
                }
                color={
                  editor.isActive({textAlign: 'left'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Center">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('center').run()
                }
                color={
                  editor.isActive({textAlign: 'center'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignCenterIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Right">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('right').run()
                }
                color={
                  editor.isActive({textAlign: 'right'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Justify">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('justify').run()
                }
                color={
                  editor.isActive({textAlign: 'justify'})
                    ? 'primary'
                    : 'default'
                }
              >
                <FormatAlignJustifyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isImageSelected && <ImageWrapButton editor={editor} />}
          </div>
        )}

        {/* Image wrap (when alignment group is hidden but an image is selected) */}
        {!showAlignmentGroup && isImageSelected && (
          <div className="html-editor-toolbar__group">
            <ImageWrapButton editor={editor} />
          </div>
        )}

        {/* Formatting */}
        {showFormattingGroup && (
          <div className="html-editor-toolbar__group">
            {has('bold') && (
              <Tooltip title="Bold">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  color={editor.isActive('bold') ? 'primary' : 'default'}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('italic') && (
              <Tooltip title="Italic">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  color={editor.isActive('italic') ? 'primary' : 'default'}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('underline') && (
              <Tooltip title="Underline">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  color={editor.isActive('underline') ? 'primary' : 'default'}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('strikethrough') && (
              <Tooltip title="Strikethrough">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  color={editor.isActive('strike') ? 'primary' : 'default'}
                >
                  <StrikethroughSIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('superscript') && (
              <Tooltip title="Superscript">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleSuperscript().run()
                  }
                  color={editor.isActive('superscript') ? 'primary' : 'default'}
                >
                  <span style={{fontSize: 12, fontWeight: 'bold'}}>
                    X<sup>2</sup>
                  </span>
                </IconButton>
              </Tooltip>
            )}

            {has('removeFormat') &&
              (has('bold') ||
                has('italic') ||
                has('underline') ||
                has('strikethrough') ||
                has('superscript')) && (
                <div className="html-editor-toolbar__divider" />
              )}

            {has('removeFormat') && (
              <Tooltip title="Remove Format">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().unsetAllMarks().clearNodes().run()
                  }
                >
                  <FormatClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* Insert */}
        {showInsertGroup && (
          <div className="html-editor-toolbar__group">
            {has('link') && (
              <ToolbarPopover
                label="URL"
                placeholder="https://..."
                defaultValue={editor.getAttributes('link').href || ''}
                onSubmit={handleLinkSubmit}
                onRemove={handleLinkRemove}
                presets={linkPresets}
              >
                {({onClick}) => (
                  <Tooltip title="Link">
                    <IconButton
                      size="small"
                      onClick={onClick}
                      color={editor.isActive('link') ? 'primary' : 'default'}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ToolbarPopover>
            )}
            {has('mediaEmbed') && (
              <ToolbarPopover
                label="Media URL"
                placeholder="YouTube, Vimeo, Wistia, etc."
                onSubmit={handleMediaEmbedSubmit}
              >
                {({onClick}) => (
                  <Tooltip title="Media Embed">
                    <IconButton size="small" onClick={onClick}>
                      <VideoLibraryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ToolbarPopover>
            )}
            {has('imageUpload') && (
              <Tooltip title="Upload Image">
                <IconButton size="small" onClick={handleImageClick}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{display: 'none'}}
              onChange={handleFileChange}
            />
            {has('htmlEmbed') && (
              <ToolbarPopover
                label="HTML Code"
                placeholder="<div>...</div>"
                onSubmit={handleHtmlEmbedSubmit}
                multiline
              >
                {({onClick}) => (
                  <Tooltip title="HTML Embed">
                    <IconButton size="small" onClick={onClick}>
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ToolbarPopover>
            )}
          </div>
        )}

        {/* Lists / Indent */}
        {showListGroup && (
          <div className="html-editor-toolbar__group">
            {has('bulletedList') && (
              <Tooltip title="Bullet List">
                <IconButton
                  size="small"
                  onClick={() => {
                    const {textAlign} = editor.getAttributes('paragraph')
                    editor.chain().focus().toggleBulletList().run()
                    if (textAlign && textAlign !== 'left') {
                      editor.chain().setTextAlign(textAlign).run()
                    }
                  }}
                  color={editor.isActive('bulletList') ? 'primary' : 'default'}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('numberedList') && (
              <Tooltip title="Ordered List">
                <IconButton
                  size="small"
                  onClick={() => {
                    const {textAlign} = editor.getAttributes('paragraph')
                    editor.chain().focus().toggleOrderedList().run()
                    if (textAlign && textAlign !== 'left') {
                      editor.chain().setTextAlign(textAlign).run()
                    }
                  }}
                  color={editor.isActive('orderedList') ? 'primary' : 'default'}
                >
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('outdent') && (
              <Tooltip title="Decrease Indent">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (editor.isActive('listItem')) {
                      editor.chain().focus().liftListItem('listItem').run()
                    } else {
                      editor.chain().focus().outdent().run()
                    }
                  }}
                >
                  <FormatIndentDecreaseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('indent') && (
              <Tooltip title="Increase Indent">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (editor.isActive('listItem')) {
                      editor.chain().focus().sinkListItem('listItem').run()
                    } else {
                      editor.chain().focus().indent().run()
                    }
                  }}
                >
                  <FormatIndentIncreaseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* Block / Table */}
        {showBlockGroup && (
          <div className="html-editor-toolbar__group">
            {has('blockQuote') && (
              <Tooltip title="Blockquote">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                  color={editor.isActive('blockquote') ? 'primary' : 'default'}
                >
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('insertTable') && (
              <TableGridPicker editor={editor}>
                {({onClick}) => (
                  <Tooltip title="Insert Table">
                    <IconButton
                      size="small"
                      onClick={onClick}
                      color={editor.isActive('table') ? 'primary' : 'default'}
                    >
                      <TableChartIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableGridPicker>
            )}
          </div>
        )}

        {/* ObvioVariable dropdown */}
        {showVariableGroup && (
          <div className="html-editor-toolbar__group">
            <select
              className="html-editor-toolbar__select"
              value=""
              onChange={handleVariableChange}
            >
              <option value="" disabled>
                {'{{ Variables }}'}
              </option>
              {variables!.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
            <CustomDateTimePopover
              anchorEl={customDateTimeEl}
              onClose={() => setCustomDateTimeEl(null)}
              onSubmit={handleCustomDateTimeSubmit}
            />
          </div>
        )}

        {/* Insert Custom Button */}
        {showCustomButtonGroup && (
          <div className="html-editor-toolbar__group">
            <Tooltip title="Insert Button">
              <IconButton size="small" onClick={handleInsertCustomButton}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    border: '1px solid currentColor',
                    borderRadius: 3,
                    padding: '1px 4px',
                    lineHeight: 1.3,
                  }}
                >
                  BTN
                </span>
              </IconButton>
            </Tooltip>
          </div>
        )}

        {/* Theme Toggle */}
        {showThemeGroup && (
          <div className="html-editor-toolbar__group">
            <select
              className="html-editor-toolbar__select"
              value={theme}
              onChange={(e) => onThemeChange(e.target.value as EditorTheme)}
            >
              <option value="Auto">Auto</option>
              <option value="Light">Light</option>
              <option value="Dark">Dark</option>
            </select>
          </div>
        )}
      </div>

      {/* Custom Button contextual toolbar */}
      {isCustomButtonSelected && <CustomButtonToolbar editor={editor} />}

      {/* Table contextual toolbar */}
      {isTableActive && !isCustomButtonSelected && (
        <TableContextToolbar editor={editor} />
      )}
    </>
  )
}
